import { initI18n } from '@/lib/i18n';

/**
 * The `clients` i18n namespace (BP-03 §9: "i18n: `clients.*`, …").
 *
 * Registered on BP-01's i18next instance with `addResourceBundle` rather than added to
 * `src/lib/i18n/resources.ts`: `lib/` is shared by both apps and knows nothing about
 * features. v2's Hindi, Marathi and Gujarati arrive as sibling bundles under the same keys.
 *
 * Nothing here is versioned. The DPDP notice is (rule 7), and it lives in
 * `features/dpdp` where it is generated from the api's own text.
 */

/** The namespace name. */
export const CLIENTS_NAMESPACE = 'clients';

/** BP-03 §9's `clients.*`. */
export const clientsStrings = {
  title: 'Clients',
  search: 'Phone or name',
  searchHint: 'Type the number first — it is how we find a record.',
  tagFilter: 'Tag',
  empty: 'No clients yet',
  choose: 'Choose a client to see their record here.',
  none: 'No client matches that.',
  offline: 'Offline — showing the last clients this device stored.',
  new: 'New client',
  open: 'Open',
  name: 'Name',
  phone: 'Mobile number',
  gender: 'Gender',
  dob: 'Date of birth',
  email: 'Email',
  notes: 'Notes',
  tags: 'Tags',
  source: 'How they found us',
  save: 'Save client',
  saved: 'Saved.',
  duplicateTitle: 'This number is already on a record',
  duplicateHint: 'Open the existing record instead of creating a second one.',
  maybeDuplicate: 'Possible duplicate',
  mergedBanner: 'This record was merged. Opening the surviving record.',
  erasedBanner: 'This record was erased at the client’s request.',
  masked: 'Your role sees the number masked.',
  tab: {
    overview: 'Overview',
    profile: 'Nail profile',
    consents: 'Consents',
    history: 'History',
    dsr: 'Data requests',
  },
  overview: {
    visits: 'Visits',
    lastVisit: 'Last visit',
    spend: 'Lifetime spend',
    avgTicket: 'Average ticket',
    noShows: 'No-shows',
    outstanding: 'Outstanding',
    stub: 'Visit history arrives with bookings and billing.',
  },
  profile: {
    title: 'Nail profile',
    shape: 'Shape',
    length: 'Length',
    finish: 'Finish',
    allergies: 'Allergies',
    sensitivities: 'Sensitivities',
    dos: 'Do',
    donts: 'Do not',
    none: 'Nothing recorded yet.',
    addAllergy: 'Add an allergy',
    severity: { mild: 'Mild', moderate: 'Moderate', severe: 'Severe' },
    save: 'Save profile',
  },
  consent: {
    title: 'Consents',
    notice: 'Show the client this notice, then record what they chose.',
    live: 'Live',
    withdrawn: 'Withdrawn',
    superseded: 'Superseded',
    needsReconfirmation: 'Ask again at the next visit',
    withdraw: 'Withdraw',
    grant: 'Record consent',
    confirmedTap: 'The client tapped',
    confirmedVerbal: 'The client said yes',
    requires: 'Needs {{purpose}} first',
    required: 'Required to keep a record at all',
    purpose: {
      service_record: 'Keep my service record',
      reminders: 'Appointment reminders',
      marketing: 'Offers and new shades',
      photo_storage: 'Store photos of my nails',
      photo_marketing: 'Show my nail photos publicly',
      ar_training: 'Use my nail photos to improve shade try-on',
    },
  },
  merge: {
    title: 'Merge two records',
    survivor: 'Keep this record',
    other: 'Merge this record into it',
    otherId: 'The id of the record to merge',
    field: 'Take {{field}} from',
    fromSurvivor: 'This record',
    fromMerged: 'The other record',
    submit: 'Merge — this cannot be undone',
    done: 'Merged. {{counts}}',
    pending: 'Not yet moved: {{modules}}',
  },
} as const;

/** The bundle as i18next holds it. */
export const clientsBundle = { clients: clientsStrings };

/** Register the namespace. Idempotent, and safe to call from several screens. */
export function registerClientsI18n(): void {
  initI18n().addResourceBundle('en', CLIENTS_NAMESPACE, clientsBundle, true, true);
}

registerClientsI18n();
